/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { renderToStaticMarkup } from 'react-dom/server';

import {
  SyncedInfoContextProps,
  SyncedInfoProvider,
  useSyncedInfo,
} from 'common/components/Autosync/SyncedInfoProvider';
import './SyncedInfo.scss';
import FormatTypeInfo, {
  showFormatTypeInfo,
  ResponseFormatTypeChange,
} from 'common/components/FormatTypeInfo/FormatTypeInfo';
import { isSVGminitableIngestionEnabled } from 'common/helpers/featureFlags';
import showAutosync, { canShowBasedOnStatus } from '../../helpers/ShowAutosync';
import ExcelPPTSyncInfo from './ExcelPPTSyncInfo';
import PowerBiSyncInfo from './PowerBiSyncInfo';
import TableauSyncInfo from './TableauSyncInfo';
import PowerPivotSyncInfo from './PowerPivotSyncInfo';

/**
 * Expected object for excelPPTItem:
 *             sharePointItemUrl: payLoad.SharePointItemUrl,
 *             fileName: renderData.fileName,
 *             slideNumber: payLoad.SlideNumber,
 *             worksheetName,
 *             formatType,
 *             libraryItemId: libraryItem.id,
 *             projectId,
 *             value,
 *             autoSync
 */

interface SyncedInfoWrappedProps {
  projectId: string;
  excelPPTItem?: unknown;
  issueFound?: boolean;
  svgMinitableShow?: boolean;
  isPwcUser: boolean;
}

interface PowerPivotProps {
  sharePointItemUrl: string;
  worksheetName: string;
  name: string;
}

interface IReport {
  name: string;
  reportId: string;
  workspaceId: string;
}

interface SyncedInfoProps extends SyncedInfoWrappedProps {
  readonly canShowLink?: boolean;
  readonly currentReport?: IReport;

  readonly handleClickFormatType?: (
    response: ResponseFormatTypeChange,
    libraryItemId: string,
    projectId: string
  ) => void;

  readonly hideSwitcherForEditor?: boolean;
  readonly isPowerBi?: boolean;
  readonly isTableau?: boolean;
  readonly isPwcUser: boolean;
  readonly powerPivot?: PowerPivotProps;
  readonly setLoading?: (libraryItemId: string, isLoading: boolean) => void;
  readonly tableauDisplayName?: string;
  readonly tableauEmbedUrl?: string;
  readonly onShowCallback?: (showSyncedInfo: boolean) => void;
}

const AUTOSYNC_TEXT_POWERBI = 'Source file link';
const SyncedInfo = ({
  canShowLink = false,
  currentReport,
  excelPPTItem = {},
  handleClickFormatType,
  hideSwitcherForEditor = false,
  isPowerBi = false,
  isTableau = false,
  issueFound = false,
  onShowCallback,
  powerPivot,
  projectId,
  setLoading,
  svgMinitableShow = true,
  tableauDisplayName = '',
  tableauEmbedUrl = '',
  isPwcUser,
}: SyncedInfoProps) => {
  const [canShow, setCanShow] = useState(canShowLink);
  const { shareWithAll, isEditMode } = useSyncedInfo();
  const { t } = useTranslation();

  const {
    // @ts-ignore
    worksheetName,
    // @ts-ignore
    groupName,
    // @ts-ignore
    type,
    // @ts-ignore
    autoSync,
    // @ts-ignore
    libraryItemId,
    // @ts-ignore
    formatType,
  } = excelPPTItem;
  const showFormatType = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      showFormatTypeInfo({ type, autoSync }) && isEditMode,
    [autoSync, type, isEditMode]
  );
  const isManualRefreshEnabled = localStorage.getItem('isManualRefreshEnabled');
  const AUTOSYNC_TEXT =
    isManualRefreshEnabled === 'true'
      ? t('generatedFrom')
      : t('autosyncedFrom');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const showPowerBi =
    canShow &&
    !isTableau &&
    !powerPivot &&
    (worksheetName || groupName || isPowerBi) &&
    canShowBasedOnStatus(shareWithAll, isEditMode);
  const showTableau = canShow && isTableau;
  const showPowerPivot =
    canShow &&
    powerPivot?.name &&
    powerPivot?.worksheetName &&
    powerPivot?.sharePointItemUrl;

  useEffect(() => {
    const setVisibility = async () => {
      // we are in Excel/PPT, but have nothing to show (probably shared version)
      // so do not show the AutoSync and stop trying
      // added in Bug 36006 - Click on switch to the shared-with-client on an analysis rendering blank white page (1.28HotFix)
      if (
        !isPowerBi &&
        !worksheetName &&
        !groupName &&
        !isTableau &&
        !powerPivot
      ) {
        setCanShow(false);
        if (onShowCallback) {
          onShowCallback(false);
        }
        return;
      }

      const showAutoSync = await showAutosync(projectId, isPwcUser);
      setCanShow(showAutoSync);

      if (
        onShowCallback &&
        showAutoSync &&
        (showPowerBi || showTableau || showPowerPivot)
      ) {
        onShowCallback(true);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setVisibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excelPPTItem, isPwcUser]);

  return (
    <>
      {showPowerBi ? (
        <div className="content-item-link">
          {isPowerBi ? AUTOSYNC_TEXT_POWERBI : AUTOSYNC_TEXT}{' '}
          {(worksheetName || groupName) && (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            <ExcelPPTSyncInfo excelPPTItem={excelPPTItem} />
          )}
          {isPowerBi && <PowerBiSyncInfo report={currentReport} />}
        </div>
      ) : (
        <></>
      )}
      {showTableau ? (
        <div className="content-item-link">
          {t('sourceFileLinkTableau')}{' '}
          <TableauSyncInfo
            displayName={tableauDisplayName}
            embedUrl={tableauEmbedUrl}
          />
        </div>
      ) : null}

      {showPowerPivot ? (
        <div className="content-item-link">
          <PowerPivotSyncInfo
            name={powerPivot.name}
            worksheetName={powerPivot.worksheetName}
            sharePointItemUrl={powerPivot.sharePointItemUrl}
          />
        </div>
      ) : null}

      {/* eslint-disable @typescript-eslint/no-unsafe-assignment */}
      <FormatTypeInfo
        show={showFormatType && !hideSwitcherForEditor}
        isEditMode
        handleClick={handleClickFormatType}
        libraryItemId={libraryItemId}
        isHTML={formatType !== 'SVG'}
        projectId={projectId}
        issueFound={issueFound}
        svgMinitableShow={svgMinitableShow}
        setLoading={setLoading}
      />
      {/* eslint-enable @typescript-eslint/no-unsafe-assignment */}
    </>
  );
};

export const syncedInfoWrapped = ({
  projectId,
  excelPPTItem,
  contextStatusInfoItem = {},
  issueFound = false,
  svgMinitableShow = isSVGminitableIngestionEnabled,
  isPwcUser,
}: SyncedInfoWrappedProps & {
  contextStatusInfoItem?: SyncedInfoContextProps;
}): string => {
  if (!excelPPTItem) return '';
  const encodedAsString = renderToStaticMarkup(
    <SyncedInfoProvider value={contextStatusInfoItem}>
      <SyncedInfo
        projectId={projectId}
        excelPPTItem={excelPPTItem}
        canShowLink
        issueFound={issueFound}
        svgMinitableShow={svgMinitableShow}
        isPwcUser={isPwcUser}
      />
    </SyncedInfoProvider>
  );
  return encodedAsString;
};

export default SyncedInfo;
